import { DateTime } from '@ksyos/date-time';
import { useNavigate } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { ServiceUrlObject } from '@common/interfaces/serviceUrlObject';
import type { IReduxState } from '../../../../../configureStore';
import type { IRequestGetOne } from '../../../../interfaces/requestGetOne';
import { EpisodeCategory } from '../../../../../../common/enums';
import { RequestStatus } from '../../../../../../common/enums/requestStatus';
import { ServiceCode } from '../../../../../../common/enums/serviceCode';
import { isActiveOnDate } from '../../../../../../common/utils/isActiveOnDate';
import { UrlBuilder } from '../../../../../../common/utils/urlBuilder';
import { serviceConsumersSelector } from '../../../../../store/serviceConsumers/serviceConsumerActions';
import { useAbortableFetch } from '../../../../../utils/hooks/useAbortableFetch';
import { Button, ButtonContainer } from '../../../../elements/common';

interface IOsasvoCreateOsasvcButtonProps {
    onServiceIdSelected?: (args: ServiceUrlObject) => Promise<void>;
    request: IRequestGetOne;
}

export function OsasvoCreateOsasvcButton(props: IOsasvoCreateOsasvcButtonProps) {
    const navigate = useNavigate();
    const [canStartOsasvcRequest, setCanStartOsasvcRequest] = useState<boolean>(false);
    const [osasvcServiceId, setOsasvcServiceId] = useState<string>();
    const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);

    const abortableFetch = useAbortableFetch();

    const activeOsasvcServiceConsumer = useSelector(
        (state: IReduxState) => serviceConsumersSelector(state).serviceConsumers
    ).find(
        (serviceConsumer) =>
            serviceConsumer.service.code === ServiceCode.OSASVC && isActiveOnDate(serviceConsumer, new Date())
    );

    useEffect(() => {
        checkDisplayCreateOsasvcButton();
    }, [props.request.status]);

    if (!canStartOsasvcRequest) {
        return null;
    }

    return (
        <ButtonContainer
            alignRight
            buttons={[
                <Button
                    automation="createOsasvc"
                    color="secondary"
                    disabled={buttonDisabled}
                    key={'createOsasvc'}
                    large
                    onClick={async () => {
                        setButtonDisabled(true);
                        navigate(getStartOsasvcUrl());
                        if (props.onServiceIdSelected) {
                            await props.onServiceIdSelected({
                                serviceId: osasvcServiceId,
                                withProcedure: false,
                            });
                        }
                        setButtonDisabled(false);
                    }}
                >
                    Start Vervolgconsult
                </Button>,
            ]}
        />
    );

    function getStartOsasvcUrl() {
        const urlBuilder = new UrlBuilder('/episode');
        urlBuilder
            .add('episodeCategory', 'somnology')
            .add('serviceId', osasvcServiceId!)
            .add('withProcedure', false)
            .add('patientId', props.request.patientId);

        return urlBuilder.build();
    }

    async function checkDisplayCreateOsasvcButton() {
        if (activeOsasvcServiceConsumer) {
            const patientSomnologyRequests = await abortableFetch.fetchJson(
                `/api/requestProcesses?patientId=${props.request.patientId}&episodeCategory=${EpisodeCategory.somnology}&orderByCreatedAtDesc=true`,
                'GET',
                undefined,
                [],
                [400]
            );
            if (
                !patientSomnologyRequests.errorCode &&
                !patientSomnologyRequests.json.items.find(
                    (item: { code: ServiceCode; created_at: Date | string; status: RequestStatus }) =>
                        item.code === ServiceCode.OSASVC &&
                        props.request.dateSent &&
                        DateTime.from(item.created_at).isAfter(props.request.dateSent) &&
                        [RequestStatus.draft, RequestStatus.completed].includes(props.request.status)
                ) &&
                props.request.status === RequestStatus.completed
            ) {
                setCanStartOsasvcRequest(true);
                setOsasvcServiceId(activeOsasvcServiceConsumer.service.id);
            }
        }
    }
}
